import {
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  useQuery,
} from '@apollo/client';
import { useMemo } from 'react';
import { useIntegratedClient } from '../useIntegratedClient';
import { AsyncState } from '../../constants/types';
import { ApolloClientKey } from '../../providers/ApolloClientsProvider';
import { useAbortController } from 'ui-components/src/hooks/useAbortController';
import { useErrorHandler } from '../useErrorHandler';

export function useIntegratedQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode,
  clientKey: ApolloClientKey,
  options?: QueryHookOptions<TData, TVariables>,
): AsyncState<TData> {
  const signal = useAbortController();
  const client = useIntegratedClient(clientKey);

  const queryOptions = useMemo<QueryHookOptions<TData, TVariables>>(
    () => ({
      client,
      fetchPolicy: 'cache-and-network',
      context: {
        fetchOptions: { signal },
      },
      ...options,
    }),
    [options, client, signal],
  );

  const queryResult = useQuery<TData, TVariables>(query, queryOptions);

  useErrorHandler(queryResult);

  return useMemo(
    () => ({
      loading: queryResult.loading,
      error: queryResult.error,
      data: queryResult.data,
      networkStatus: queryResult.networkStatus,
    }),
    [
      queryResult.loading,
      queryResult.error,
      queryResult.data,
      queryResult.networkStatus,
    ],
  );
}
